if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const btn = mediaToSwitch.querySelector('button');
            if (btn) {
              btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
            }
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      getMainSwiper() {
        return this.elements.viewer?.querySelector('.product-media-gallery__swiper--main')?.swiper;
      }

      getThumbSwiper() {
        return this.elements.thumbnails?.querySelector('.product-media-gallery__swiper--thumbs')?.swiper;
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails || !event.detail?.currentElement?.dataset?.mediaId) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const mainSwiper = this.getMainSwiper();
        const activeMedia =
          this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer?.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        const slideEl = activeMedia.closest('.swiper-slide') || activeMedia;

        if (mainSwiper) {
          if (prepend) {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              if (activeThumbnail?.parentElement) {
                activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                  activeThumbnail.parentElement.prepend(activeThumbnail);
              }
            }
            requestAnimationFrame(() => {
              mainSwiper.update();
              this.getThumbSwiper()?.update?.();
              mainSwiper.slideTo(0, 0, false);
              mainSwiper.slides.forEach((s, i) => {
                s.classList.toggle('is-active', i === mainSwiper.activeIndex);
              });
              if (mainSwiper.pagination) mainSwiper.pagination.update();
            });
          } else {
            const idx = [...mainSwiper.slides].indexOf(slideEl);
            if (idx >= 0) mainSwiper.slideTo(idx, 0, false);
            mainSwiper.slides.forEach((s, i) => {
              s.classList.toggle('is-active', i === mainSwiper.activeIndex);
            });
            requestAnimationFrame(() => {
              mainSwiper.update();
              this.getThumbSwiper()?.update?.();
              if (mainSwiper.pagination) mainSwiper.pagination.update();
            });
          }
        } else {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
            element.classList.remove('is-active');
          });
          activeMedia.classList.add('is-active');

          if (prepend) {
            activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              if (activeThumbnail?.parentElement) {
                activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                  activeThumbnail.parentElement.prepend(activeThumbnail);
              }
            }

            if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!mainSwiper && (!this.mql.matches || this.elements.thumbnails)) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        const pos = activeThumbnail.dataset.mediaPosition;
        if (pos) this.announceLiveRegion(activeMedia, pos);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', 'true');

        const ts = this.getThumbSwiper();
        if (ts) {
          const slide = thumbnail.classList.contains('swiper-slide') ? thumbnail : thumbnail.closest('.swiper-slide');
          if (slide) {
            const idx = ts.slides.indexOf(slide);
            if (idx >= 0) ts.slideTo(idx, 200);
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;

        const thumbSlider = this.elements.thumbnails.slider;
        const flexDir = thumbSlider ? window.getComputedStyle(thumbSlider).flexDirection : '';
        if (flexDir === 'column') {
          thumbSlider.scrollTo({ top: thumbnail.offsetTop, behavior: 'smooth' });
        } else if (thumbSlider) {
          thumbSlider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        const list = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        if (!list) return;
        list.setAttribute('role', 'presentation');
        list.querySelectorAll('.swiper-slide, [id^="Slide-"]').forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  );
}
